import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import _, { isEqual } from "lodash";
import {
  getOrganizationSpecialJobTitles,
  createSpecialJobTitle,
  updateSpecialJobTitle,
  deleteSpecialJobTitle as deleteSpecialJobTitleService
} from "../../../service";
import { PlusIcon, RemoveIcon } from "../../../asssets/icons";
import { Spinner } from "../../form";
import { Button } from "../../form";
import { toast } from "react-toastify";
import { BaseModal } from "../../popups";
import { FaTrash } from "react-icons/fa";

const AddOrgSpecialJobTitles = ({ org }) => {
  const [originalItems, setOriginalItems] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const { orgId } = useParams();

  const emptyItem = {
    _id: "",
    name_fr: "",
    name_de: "",
    name_en: "",
    name_it: "",
    male_fr: "",
    male_de: "",
    male_en: "",
    male_it: "",
    female_fr: "",
    female_de: "",
    female_en: "",
    female_it: "",
    orderKey: 0
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data } = await getOrganizationSpecialJobTitles(orgId);
      data.length > 0 ? setItems(data) : setItems([{ ...emptyItem }]);
      setOriginalItems(data);
    } catch (error) {
      setOriginalItems([]);
      console.log(error);
      toast.error("Error fetching special job titles");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, [orgId, org]);

  const addItem = () => {
    setItems([...items, { ...emptyItem, orderKey: items.length }]);
  };

  const handleDeleteClick = (item, index) => {
    const itemName =
      item.name_en || item.name_fr || item.name_de || item.name_it || `Title #${index + 1}`;
    setDeleteItem({ ...item, title: itemName, index });
  };

  const confirmDelete = async (data) => {
    const { _id, index } = data;
    let updated = [...items];

    try {
      if (_id !== undefined) {
        await deleteSpecialJobTitleService(orgId, _id);
        toast.success("Special job title deleted successfully");
      }
      updated = updated.filter((_, i) => i !== index);
      setItems(updated);
    } catch (error) {
      console.log(error);
      toast.error("Error deleting special job title");
    }

    setDeleteItem(null);
  };

  const handleChange = (index, field, value) => {
    const updated = _.cloneDeep(items);
    updated[index][field] = value;
    setItems(updated);
  };

  const saveItems = async () => {
    try {
      setSaving(true);
      for (const [index, item] of items.entries()) {
        if (item._id !== undefined) {
          const original = originalItems.find((o) => o._id === item._id);
          if (!isEqual(item, original)) {
            setSaving(`Updating title #${index + 1}...`);
            await updateSpecialJobTitle(orgId, item._id, item);
          }
        } else {
          setSaving(`Adding title #${index + 1}...`);
          await createSpecialJobTitle({ ...item, orderKey: index }, orgId);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Error saving special job titles");
    } finally {
      setSaving("Saved successfully");
      setSaving(false);
      fetchItems();
    }
  };

  if (loading)
    return (
      <div className="text-center">
        <Spinner size={16} color="#515151" isSubmitting={loading} />
      </div>
    );

  return (
    <>
      <div className="container mx-auto p-4">
        <div className="flex justify-end mt-4 fixed top-4 right-4 z-50">
          <button
            onClick={saveItems}
            type="button"
            disabled={saving}
            className="flex flex-col gap-2 disabled:opacity-60 select-none py-2 font-bold text-md border bg-[#515151] text-[#FFFFFF] px-8 rounded-3xl">
            <span className="flex items-center gap-2">
              Save Job Titles{" "}
              {saving && <Spinner isSubmitting={saving} size={16} color="#FFFFFF" />}
            </span>
            {saving && (
              <span className="block">
                <span className="text-xs text-gray-300">
                  {typeof saving === "string" ? saving : "Saving..."}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {items.map((item, index) => (
          <div key={index} className="container mx-auto">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">
                  Special Job Title #{index + 1}
                </h3>
                <button
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  onClick={() => handleDeleteClick(item, index)}>
                  <RemoveIcon />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {/* Order Key */}
                <div className="w-1/4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Order Key</label>
                  <input
                    type="number"
                    value={item.orderKey ?? ""}
                    onChange={(e) => handleChange(index, "orderKey", parseInt(e.target.value) || 0)}
                    placeholder="Order"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                  />
                </div>

                {/* Language Fields */}
                <div className="space-y-6">
                  <h4 className="text-md font-medium text-gray-700 border-b pb-2">
                    Language Content
                  </h4>

                  {org.languages?.map((language, languageIndex) => (
                    <div
                      key={languageIndex}
                      className="space-y-4 border border-gray-200 rounded-lg p-4">
                      <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wide bg-gray-100 px-2 py-1 rounded">
                        {language}
                      </h5>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Name
                          </label>
                          <input
                            type="text"
                            value={item[`name_${language}`] || ""}
                            onChange={(e) =>
                              handleChange(index, `name_${language}`, e.target.value)
                            }
                            placeholder={`Name (${language.toUpperCase()})`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Male
                          </label>
                          <input
                            type="text"
                            value={item[`male_${language}`] || ""}
                            onChange={(e) =>
                              handleChange(index, `male_${language}`, e.target.value)
                            }
                            placeholder={`Male (${language.toUpperCase()})`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Female
                          </label>
                          <input
                            type="text"
                            value={item[`female_${language}`] || ""}
                            onChange={(e) =>
                              handleChange(index, `female_${language}`, e.target.value)
                            }
                            placeholder={`Female (${language.toUpperCase()})`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 w-full">
        <div className="flex justify-end mt-8 w-full">
          <button
            className="flex items-center gap-2 px-6 py-3 bg-blue-200 text-[#606060] text-xl font-bold rounded-lg hover:bg-blue-400 transition-colors shadow-sm"
            onClick={addItem}>
            <span className="w-8 h-8">
              <PlusIcon />
            </span>
            <span>Add New Special Job Title</span>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <BaseModal
        open={deleteItem ? true : false}
        setOpen={setDeleteItem}
        title={`Are you sure you want to delete '${deleteItem?.title}'`}>
        {deleteItem && (
          <>
            <p className="text-base text-black">This action cannot be undone</p>
            <Button.ButtonOutlined
              className={
                "border-red-500 flex items-center justify-center w-full text-red-500 gap-2 mt-2"
              }
              handler={() => confirmDelete(deleteItem)}>
              <span className="w-4">
                <FaTrash />
              </span>
              Delete
            </Button.ButtonOutlined>
          </>
        )}
      </BaseModal>
    </>
  );
};

export default AddOrgSpecialJobTitles;
